/**
 * OPUS-1045 — unified fixture picker.
 *
 * Replaces the FIFA/PLS source pill + the two per-source pickers with a single
 * picker that pulls from BOTH sources and merges them:
 *   - a Competition dropdown listing BOTH sources' competitions by name
 *     (FIFA's discovered competitions + PLS's DDS competitions — Premier
 *     League, J1 League, …), plus an "All competitions" option that fetches
 *     both sources in PARALLEL,
 *   - a Season selector (shown for a specific PLS competition when DDS
 *     returns seasons for it),
 *   - a calendar Date filter that dots the days carrying a fixture,
 *   - a single Fixture selector over the merged list.
 *
 * Every fixture row is tagged with its `source` ('fifa' | 'pls'); onSelect hands
 * that source back so the parent can bind through the correct data service
 * (the selected fixture's source — see SceneControls' useFixtureContext).
 *
 * Listing is read-only (catalogue.listFixtures / getCompetitions); this component
 * does not touch broker/live commands.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, TextField, MenuItem, CircularProgress, Typography, Popover } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FixtureCalendar from "./FixtureCalendar";

const LABEL_SX = { color: "var(--text-muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em", minWidth: 80 };
const FIELD_SX = {
  "& .MuiInputBase-input": { color: "var(--text)" },
  "& .MuiSelect-icon": { color: "var(--text-muted)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-strong)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--green)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--green)" },
};
const MENU_PROPS = {
  MenuProps: {
    PaperProps: {
      sx: {
        backgroundColor: "var(--elevated)", color: "var(--text)",
        "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(69, 255, 167, 0.1)" },
        "& .Mui-selected": { backgroundColor: "rgba(69, 255, 167, 0.15) !important" },
      },
    },
  },
};

const matchesAllTokens = (opt, tokens) => {
  if (!tokens.length) return true;
  const hay = `${opt.label || ""} ${opt.competitionLabel || ""}`.toLowerCase();
  return tokens.every((t) => hay.includes(t));
};
const dayOf = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

export default function MergedFixturePicker({
  fifaCatalogue,
  plsCatalogue,
  fifaCompetitions = [],
  fifaCompetitionId,
  onFifaCompetitionChange,
  plsCompetitions = [],
  plsCompetitionId,
  onPlsCompetitionChange,
  plsSeasons = [],
  plsSeasonId,
  onPlsSeasonChange,
  selectedFixtureId,
  onSelect,
  size = "small",
}) {
  // 'all' | 'fifa:<competitionId>' | 'pls:<competitionId>'
  const [competitionKey, setCompetitionKey] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [dateAnchor, setDateAnchor] = useState(null); // calendar popover anchor
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const competitionOptions = useMemo(() => {
    const opts = [{ key: "all", label: "All competitions" }];
    fifaCompetitions.forEach((c) => opts.push({ key: `fifa:${c.id}`, label: c.name, source: "fifa", id: c.id }));
    // OPUS-1045: PLS competitions are now discovered from DDS by name
    // (Premier League, J1 League, …) rather than a single "PLS" entry.
    plsCompetitions.forEach((c) => opts.push({ key: `pls:${c.id}`, label: c.name, source: "pls", id: c.id }));
    // Fallback while discovery is still resolving but a PLS competition is
    // already bound — keeps the picker usable on first paint.
    if (!plsCompetitions.length && plsCompetitionId) {
      opts.push({ key: `pls:${plsCompetitionId}`, label: "PLS", source: "pls", id: plsCompetitionId });
    }
    return opts;
  }, [fifaCompetitions, plsCompetitions, plsCompetitionId]);

  // Keep the chosen competition active upstream so the source's catalogue is
  // (re)built for it — FIFA's catalogue is competition-scoped, and PLS's is
  // competition+season-scoped (drives both the fixture list AND the bind).
  const handleCompetitionChange = (key) => {
    setCompetitionKey(key);
    if (key.startsWith("fifa:")) {
      const id = key.slice("fifa:".length);
      if (id && id !== fifaCompetitionId && typeof onFifaCompetitionChange === "function") {
        onFifaCompetitionChange(id);
      }
    } else if (key.startsWith("pls:")) {
      const id = key.slice("pls:".length);
      if (id && id !== plsCompetitionId && typeof onPlsCompetitionChange === "function") {
        onPlsCompetitionChange(id);
      }
    }
  };

  // Fetch + merge fixtures whenever the selection or a catalogue changes.
  useEffect(() => {
    let cancelled = false;
    const tag = (rows, source, competitionLabel) =>
      (rows || []).map((r) => ({ ...r, source, competitionLabel }));

    const fifaLabel = fifaCompetitions.find((c) => c.id === fifaCompetitionId)?.name || "FIFA";
    const plsLabel = plsCompetitions.find((c) => c.id === plsCompetitionId)?.name || "PLS";
    const wantFifa = competitionKey === "all" || competitionKey.startsWith("fifa:");
    const wantPls = competitionKey === "all" || competitionKey.startsWith("pls:");

    const jobs = [];
    if (wantFifa && fifaCatalogue) jobs.push(fifaCatalogue.listFixtures().then((r) => tag(r, "fifa", fifaLabel)));
    if (wantPls && plsCatalogue) jobs.push(plsCatalogue.listFixtures().then((r) => tag(r, "pls", plsLabel)));

    if (!jobs.length) { setFixtures([]); return undefined; }
    setLoading(true);
    setError(null);
    Promise.all(jobs)
      .then((lists) => { if (!cancelled) setFixtures(lists.flat()); })
      .catch((e) => { if (!cancelled) { setError(e?.message || String(e)); setFixtures([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [competitionKey, fifaCatalogue, plsCatalogue, fifaCompetitionId, fifaCompetitions, plsCompetitionId, plsCompetitions, plsSeasonId]);

  // The date filter is scoped to the current competition/season's fixtures —
  // drop it when either changes so a stale day can't blank the list.
  useEffect(() => { setDateFilter(""); }, [competitionKey, plsSeasonId]);

  const dateOptions = useMemo(
    () => Array.from(new Set(fixtures.map((f) => dayOf(f.startDateTime)).filter(Boolean))).sort(),
    [fixtures],
  );
  const visibleFixtures = useMemo(
    () => (dateFilter ? fixtures.filter((f) => dayOf(f.startDateTime) === dateFilter) : fixtures),
    [fixtures, dateFilter],
  );
  const selected = visibleFixtures.find((f) => f.id === selectedFixtureId)
    || fixtures.find((f) => f.id === selectedFixtureId) || null;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Typography sx={LABEL_SX}>Competition</Typography>
        <TextField select size={size} fullWidth value={competitionKey}
          onChange={(e) => handleCompetitionChange(e.target.value)}
          sx={FIELD_SX} SelectProps={MENU_PROPS}>
          {competitionOptions.map((o) => (
            <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
          ))}
        </TextField>
      </Box>

      {/* OPUS-1045: season selector — only for a specific PLS competition, and
          only when DDS actually returns seasons for it. */}
      {competitionKey.startsWith("pls:") && plsSeasons.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Typography sx={LABEL_SX}>Season</Typography>
          <TextField select size={size} fullWidth value={plsSeasonId || ""}
            onChange={(e) => onPlsSeasonChange?.(e.target.value)}
            sx={FIELD_SX} SelectProps={MENU_PROPS}>
            {plsSeasons.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {/* OPUS-1045: date filter opens a calendar popover on click (days with a
          fixture carry a coral dot and are the only selectable cells). */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Typography sx={LABEL_SX}>Date</Typography>
        <TextField
          select={false}
          size={size}
          fullWidth
          value={dateFilter ? new Date(`${dateFilter}T00:00:00`).toLocaleDateString() : "All dates"}
          onClick={(e) => setDateAnchor(e.currentTarget)}
          sx={{ ...FIELD_SX, "& .MuiInputBase-root": { cursor: "pointer" }, "& input": { cursor: "pointer" } }}
          InputProps={{
            readOnly: true,
            endAdornment: <CalendarMonthIcon sx={{ color: "var(--text-muted)", fontSize: 18 }} />,
          }}
        />
      </Box>
      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { backgroundColor: "transparent", boxShadow: 6, mt: 0.5, overflow: "visible" } } }}
      >
        <Box sx={{ width: 280 }}>
          <FixtureCalendar
            markedDates={dateOptions}
            value={dateFilter}
            onChange={(d) => { setDateFilter(d); if (d) setDateAnchor(null); }}
          />
        </Box>
      </Popover>

      <Autocomplete
        fullWidth
        size={size}
        options={visibleFixtures}
        loading={loading}
        value={selected}
        onChange={(_, next) => onSelect(next ? next.id : null, next ? next.label : null, next ? next.source : null)}
        getOptionLabel={(opt) => {
          if (!opt) return "";
          const date = opt.startDateTime ? ` (${new Date(opt.startDateTime).toLocaleDateString()})` : "";
          return `${opt.label}${date}`;
        }}
        isOptionEqualToValue={(opt, val) => opt.id === val.id && opt.source === val.source}
        filterOptions={(opts, state) => {
          const tokens = state.inputValue.toLowerCase().split(/\s+/).filter(Boolean);
          return opts.filter((opt) => matchesAllTokens(opt, tokens));
        }}
        renderInput={(params) => (
          <TextField {...params} label="Fixture" variant="outlined" sx={FIELD_SX}
            InputProps={{
              ...params.InputProps,
              endAdornment: (<>{loading ? <CircularProgress color="inherit" size={18} /> : null}{params.InputProps.endAdornment}</>),
            }} />
        )}
        renderOption={(props, option) => {
          const { key: _ignored, ...rest } = props;
          return (
            <li key={`${option.source}:${option.id}`} {...rest}>
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</span>
                <span style={{ fontSize: "0.7rem", color: "var(--text-dim)" }}>
                  {(option.source || "").toUpperCase()}{option.competitionLabel ? ` · ${option.competitionLabel}` : ""}
                  {option.startDateTime ? ` · ${new Date(option.startDateTime).toLocaleDateString()}` : ""}
                </span>
              </Box>
            </li>
          );
        }}
        noOptionsText={error ? `Error: ${error}` : "No fixtures match"}
      />
    </Box>
  );
}
